// ViewToolbarViewChips.jsx

import React, { useMemo } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { buildViewSelectorOptions } from 'utilities/ViewSelectorOptions';
import { getDefaultViewTypeIconKey } from 'utilities/ViewTypeIcons';

// CSS
import './ViewToolbarViewChips.css';

// Icons
import Icon from 'svg/Icon';

/**
 * ViewToolbarViewChips
 *
 * Horizontally scrollable one-click chips for all saved views of the active type.
 *
 * @param {Array} views - Saved views (catch-all already excluded by callers).
 * @param {string|null} activeViewId - Active saved view id (null on catch-all).
 * @param {function} onChangeView - Called with the selected view id.
 * @returns {ReactNode|null}
 */
const ViewToolbarViewChips = React.memo(({
    views = [],
    activeViewId = null,
    onChangeView,
}) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();

    // ----- MEMOS ----- //

    const chips = useMemo(() => (
        buildViewSelectorOptions(views, {
            fallbackType: 'LIST',
            includeAddOption: false,
        })
    ), [views]);

    const normalizedActiveId = String(activeViewId || '').trim();

    if (!chips.length) return null;

    // ----- RENDER ----- //

    return (
        <div className="view-toolbar-view-chips-container">

            {/* SCROLLER */}
            <div className="view-toolbar-view-chips-scroller">
                {chips.map((chip) => {
                    const isActive = Boolean(normalizedActiveId) && chip.id === normalizedActiveId;
                    const iconColor = isActive ? theme.foreground : theme.foregroundMuted;

                    return (
                        <button
                            key={chip.id}
                            type="button"
                            className={`view-toolbar-view-chip${isActive ? ' active' : ''}`}
                            style={{
                                backgroundColor: isActive
                                    ? theme.panel
                                    : 'transparent',
                                color: isActive ? theme.foreground : theme.foregroundMuted,
                            }}
                            title={chip.note || chip.label}
                            onClick={() => onChangeView?.(chip.id)}
                        >
                            <Icon
                                iconKey={getDefaultViewTypeIconKey(chip.type, 'format_list_bulleted')}
                                width={16}
                                height={16}
                                color={iconColor}
                            />

                            {/* LABEL */}
                            <span className="view-toolbar-view-chip-label">
                                {chip.label}
                            </span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
});

// Dev Tools
ViewToolbarViewChips.displayName = 'ViewToolbarViewChips';

export default ViewToolbarViewChips;
